import type { ISwipeActionProps } from '@sandstreamdev/react-swipeable-list';
import { SwipeableListItem } from '@sandstreamdev/react-swipeable-list';
import { Paragraph } from '@zillow/constellation-10';
import { memo, useContext, useMemo } from 'react';
import { Folder, ReportConversationReason } from '../../../../types/GraphqlTypes';
import StringConstants from '../../../../constants/StringConstants';
import useConversationActions from '../../../../hooks/useConversationActions';
import { IconArchived, IconMoveToInbox, IconUnread, IconUnspam } from '../../../Icons';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import stringUtils from '../../../../utils/stringUtils';
import { StyledSwipeContainer } from './StyledComponents';

type SwipeObjectForFolder = {
  text: string;
  icon: JSX.Element;
};

const MOBILE_SWIPE_RIGHT_CONTENT_MAP: Record<Folder, SwipeObjectForFolder | null> = {
  [Folder.Archived]: {
    text: StringConstants.ACTIONS.MOVE_TO_INBOX,
    icon: <IconMoveToInbox />,
  },
  [Folder.Spam]: {
    text: StringConstants.ACTIONS.NOT_SPAM,
    icon: <IconUnspam isGray={true} />,
  },
  [Folder.Inbox]: {
    text: StringConstants.ACTIONS.MARK_UNREAD,
    icon: <IconUnread />,
  },
  // Unused, must include all Folder options to use as a key
  [Folder.ShowAll]: null,
  [Folder.Favorite]: null,
};

type Props = {
  children: JSX.Element;
  setConversationBlur(shouldBlur: boolean): void;
  conversationId: string;
};

const SwipeableConversationItem = ({ children, setConversationBlur, conversationId }: Props) => {
  const { folder } = useContext(InboxPageContext);
  const { archiveConversation, undoArchiveConversation, undoReportConversation, markConversationAsUnread } =
    useConversationActions();

  /**
   * swipeLeft action only when on Folder.Inbox
   */
  const swipeLeft: ISwipeActionProps | undefined = useMemo(() => {
    if (folder === Folder.Inbox) {
      return {
        content: (
          <StyledSwipeContainer justifyContent="end" $isArchiveContent={true}>
            <IconArchived isGray={true} />
            <Paragraph textStyle="body-xs" css={{ marginLeft: 'tighter' }}>
              {stringUtils.capitalize(StringConstants.ACTIONS.ARCHIVE)}
            </Paragraph>
          </StyledSwipeContainer>
        ),
        action: () => {
          archiveConversation({
            conversationIdToUpdate: conversationId,
          });
          setConversationBlur(true);
        },
      };
    }

    return undefined;
  }, [folder]);

  const swipeRightContent = MOBILE_SWIPE_RIGHT_CONTENT_MAP[folder];
  const swipeRightAction = () => {
    switch (folder) {
      case Folder.Archived: {
        undoArchiveConversation({ conversationIdToUpdate: conversationId });
        setConversationBlur(true);
        return;
      }
      case Folder.Spam: {
        undoReportConversation({
          conversationIdToUpdate: conversationId,
          reportReasonType: ReportConversationReason.NotReported,
        });
        setConversationBlur(true);
        return;
      }
      case Folder.Inbox: {
        markConversationAsUnread({ conversationIdToUpdate: conversationId });
        setConversationBlur(false);
        return;
      }
    }
  };

  const swipeRight: ISwipeActionProps = {
    content: (
      <StyledSwipeContainer justifyContent="start" $isMoveToInboxContent={folder === Folder.Inbox}>
        {swipeRightContent?.icon}
        <Paragraph textStyle="body-xs" css={{ marginLeft: 'tighter' }}>
          {swipeRightContent?.text}
        </Paragraph>
      </StyledSwipeContainer>
    ),
    action: swipeRightAction,
  };

  return (
    <SwipeableListItem swipeLeft={swipeLeft} swipeRight={swipeRight}>
      {children}
    </SwipeableListItem>
  );
};

export default memo(SwipeableConversationItem);
